import { type MouseEvent, useEffect, useState } from 'react';
import Functions from '../../functions';
import styles from './FlickrBadge.module.css';

interface FlickrFeedItem {
    title: string;
    link: string;
    media: {
        m: string;
    };
}

interface FlickrFeed {
    items: FlickrFeedItem[];
}

const FLICKR_URL = 'https://www.flickr.com';
const FLICKR_FEED_URL = 'https://api.flickr.com/services/feeds/photos_public.gne';
const PHOTOS_SHOWN = 3;
const ROTATE_INTERVAL_MS = 60000;
const FADE_MS = 1000;

/**
 * The public photo feed only answers as JSONP, calling the global
 * `jsonFlickrFeed` it names; there is no CORS-enabled JSON variant.
 */
const loadFeed = (tags: string): Promise<FlickrFeed> =>
    new Promise((resolve, reject) => {
        const params = new URLSearchParams({ tags, format: 'json' });
        const script = document.createElement('script');
        const global = window as unknown as { jsonFlickrFeed?: (data: FlickrFeed) => void };
        const cleanup = () => {
            delete global.jsonFlickrFeed;
            script.remove();
        };
        global.jsonFlickrFeed = (data) => {
            cleanup();
            resolve(data);
        };
        script.onerror = () => {
            cleanup();
            reject(new Error('failed to load the Flickr feed'));
        };
        script.src = `${FLICKR_FEED_URL}?${params.toString()}`;
        document.body.appendChild(script);
    });

const pickRandom = (items: FlickrFeedItem[]): FlickrFeedItem[] => {
    const picked = new Set<number>();
    const max = Math.min(PHOTOS_SHOWN, items.length);
    while (picked.size < max) {
        picked.add(Math.floor(Math.random() * items.length));
    }
    return [...picked].map((i) => items[i]);
};

interface Props {
    tags: string;
}

const FlickrBadge = (props: Props) => {
    const { tags } = props;
    const [items, setItems] = useState<FlickrFeedItem[]>([]);
    const [visible, setVisible] = useState(false);
    const [showWhat, setShowWhat] = useState(false);

    useEffect(() => {
        let active = true;
        let interval: number | undefined;
        let fade: number | undefined;
        loadFeed(tags)
            .then((feed) => {
                if (!active) {
                    return;
                }
                setItems(pickRandom(feed.items));
                setVisible(true);
                // Fade the current photos out, swap them, then fade back in.
                interval = window.setInterval(() => {
                    setVisible(false);
                    fade = window.setTimeout(() => {
                        setItems(pickRandom(feed.items));
                        setVisible(true);
                    }, FADE_MS);
                }, ROTATE_INTERVAL_MS);
            })
            .catch((error: unknown) => {
                console.log(error);
            });
        return () => {
            active = false;
            window.clearInterval(interval);
            window.clearTimeout(fade);
        };
    }, [tags]);

    const handleClickWhat = (e: MouseEvent<HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setShowWhat(true);
    };

    const tagsUrl = `${FLICKR_URL}/photos/tags/${Functions.escape(tags.replace(',', ' '))}`;
    const tagsLabel = tags.replace(',', ' and ');
    return (
        <div className={styles.flickr}>
            <div className={styles.badge}>
                <div className={styles.header}>
                    <a href={FLICKR_URL} target="_blank" rel="noopener noreferrer">
                        www.
                        <span style={{ fontWeight: 'bold', color: '#3993ff' }}>
                            flick<span style={{ color: '#ff1c92' }}>r</span>
                        </span>
                        .com
                    </a>
                </div>
                <div className={styles.frame}>
                    <div className={visible ? styles.itemsShown : styles.itemsHidden}>
                        {items.map((item) => (
                            <div key={item.link} className={styles.item}>
                                <a href={item.link} title={item.title} target="_blank" rel="noopener noreferrer">
                                    <img className={styles.thumbnail} src={item.media.m} alt={item.title} />
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
                <div className={styles.footer}>
                    {showWhat ? (
                        <span>
                            This is a Flickr badge showing public photos from Flickr tagged with{' '}
                            <a href={tagsUrl} target="_blank" rel="noopener noreferrer">
                                {tagsLabel}
                            </a>
                            .
                        </span>
                    ) : (
                        <a href={FLICKR_URL} onClick={handleClickWhat}>
                            what is this?
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FlickrBadge;
